import { PATHS, alarmDetailPath } from '@ctcells/core'
import { t } from '@ctcells/i18n'
import type { ReactNode } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router'

import { cx } from '../atoms/cx.ts'
import { AddModal } from '../atoms/AddModal.tsx'
import { AlarmDetailContainer, AlarmsContainer } from '../screens/alarms/AlarmsContainer.tsx'
import { BATCHES_LIST_FIELDS, HISTORY_LIST_FIELDS } from '../screens/batches/batchListedFields.ts'
import { BatchDetailContainer } from '../screens/batches/BatchDetailContainer.tsx'
import { BatchNewContainer } from '../screens/batches/BatchNewContainer.tsx'
import { MonitoringContainer } from '../screens/monitoring/MonitoringContainer.tsx'
import { MonitoringSampleDrawerContainer } from '../screens/monitoring/MonitoringSampleDrawerContainer.tsx'

import { BatchesRoute, HistoryRoute } from './consoleBatchViews.tsx'
import { monitoringDeviceKey } from '../screens/monitoring/monitoringModel.ts'

/**
 * 콘솔 「운영」 그룹 라우트가 꽂는 **얇은 어댑터 컴포넌트**들.
 *
 * 🔴 **화면 컴포넌트는 라우터를 모른다.** 이동은 전부 여기서 `onXxx` 로 넣는다 —
 *    그래야 같은 화면을 패드·태블릿에서도 다른 이동 규칙으로 쓸 수 있다.
 *
 * 라우트 표(`consoleOpsRoutes.tsx`)와 파일을 나눈 것은 **한 파일이 컴포넌트만 내보내야**
 * 개발 서버 핫리로드가 성립하기 때문이다(`react-refresh/only-export-components`).
 * 표는 배열만, 여기는 컴포넌트만 내보낸다.
 *
 * 🔴 **권한을 여기서 끼우지 않는다**(2026-09-20). `DEMO_ACCESS` 자리표시자를 걷었다 —
 *    세 라우트가 전부 컨테이너를 꽂고, 컨테이너가 `useScreenAccess()` 로 **실세션**을 읽는다.
 */
/**
 * 모니터링 + 오른쪽 시료 상세 판(eric 2026-09-27 「해당 시료에 대한 것을 클릭하면 오른쪽에 상세 페이지가
 * 나오게 해줘. 지금은 링크로 넘어가는데, 넘어가지 말고」). 시료 목록의 `SamplesSplitRoute` 와 같은 뼈대다.
 * 🔴 주소는 **모니터링 아래**다 — `/monitoring[/:moduleKey]/sample/:sampleId`. 고른 장비 탭(`:moduleKey`)을
 *    잃지 않게 판을 열고 닫아도 모듈 키가 그대로 실린다. 시료 화면(`/samples/:id`)으로 넘어가지 않는다.
 * 🔴 서랍 내용은 **모니터링 전용**(`MonitoringSampleDrawerContainer` · 2026-09-28)이다 — 위에 그 시료의 챔버 3D, 아래에
 *    시료 목록 서랍과 같은 상세 조각(`SampleDetailScreen`)을 얹는다. 시료 목록(`/samples/:id`)의 서랍은 종전 그대로다.
 */
export function MonitoringRoute() {
  const navigate = useNavigate()
  const params = useParams()
  const deviceKey = monitoringDeviceKey(params['moduleKey'])
  const sampleId = params['sampleId']
  /* 판을 연 채 모듈 탭을 옮겨도 이 줄 하나가 경로를 짓는다 — 모듈 키가 없으면 `/monitoring` 그대로 */
  const base = deviceKey === undefined ? PATHS.monitoring : `${PATHS.monitoring}/${deviceKey}`
  /*
   * 🔴 **소모품 상세 팝오버는 걷었다**(eric 2026-10-02 「시약이나 소모품 클릭하면 팝업이 나오는데.. 안 나오게 해줘」).
   *    옛 주소(`/monitoring[/:moduleKey]/supply/:supplyKey` — 새로고침 · 옛 링크)는 라우트 표에 남아 있어 여기로 오고,
   *    그 장비의 모니터링으로 바꿔 세운다(`replace` — 이력에 안 남는다). 보충은 판 머리의 「보충」 버튼이 연다.
   */
  if (params['supplyKey'] !== undefined) return <Navigate replace to={base} />
  /*
   * 🔴 **시료 서랍은 모니터링 화면 «안»에 선다 — 선택기 줄 아래 행의 오른쪽 칸**(eric 2026-09-28 「소비 부분, 시료, 장비
   *    버튼 이거는 여전히 고정.. 그 아래 영역에서.. 상세 페이지가 나오도록」). 종전에는 이 라우트가 `.splitpage--open`
   *    두 칸으로 화면 «옆»에 세워 선택기 줄까지 밀어냈다. 이제는 서랍 한 장을 `drawer` 로 내려보내기만 한다.
   */
  const close = () => void navigate(base)
  const drawer =
    sampleId !== undefined ? (
      <aside aria-label={t('screen.sampleDetail.title')} className="splitpage__pane" key={sampleId}>
        <MonitoringSampleDrawerContainer onClose={close} sampleId={sampleId} />
      </aside>
    ) : null

  return (
    <div className="splitpage">
      <MonitoringView
        base={base}
        drawer={drawer}
        {...(deviceKey === undefined ? {} : { deviceKey })}
        {...(sampleId === undefined ? {} : { selectedSampleId: sampleId })}
      />
    </div>
  )
}

function MonitoringView({
  base,
  drawer,
  deviceKey,
  selectedSampleId,
}: {
  base: string
  drawer: ReactNode
  deviceKey?: ReturnType<typeof monitoringDeviceKey>
  selectedSampleId?: string
}) {
  const navigate = useNavigate()
  /*
   * 🔴 **장비를 옮겨도 열린 판은 그대로다.** 자동 전환(원심 → 어셈블러)이 판을 닫으면 사람이 읽던 시료 상세가
   *    배치가 넘어가는 순간 사라진다 — 판을 닫는 것은 사람의 × 뿐이다.
   */
  const deviceTo = (key: string) =>
    selectedSampleId !== undefined
      ? `${PATHS.monitoring}/${key}/sample/${encodeURIComponent(selectedSampleId)}`
      : `${PATHS.monitoring}/${key}`
  return (
    <MonitoringContainer
      {...(deviceKey === undefined ? {} : { deviceKey })}
      {...(selectedSampleId === undefined ? {} : { selectedSampleId })}
      drawer={drawer}
      /*
       * 소모품 요약(선택기 줄 카드 · 시료별 보기 오른쪽 판). 🔴 칩 · 판 머리는 눌러지지 않는다 — 상세 팝오버를 걷었다
       * (eric 2026-10-02). 팁 · TC 의 「보충」 버튼만 확인 창을 연다(컨테이너가 판정을 짓는다).
       */
      supplies
      /*
       * 🔴 **[시료][장비] 두 탭** — eric 지시 2026-09-21 「오른쪽 위에 전체·트레이·원심분리기·비전
       *    있는 것을 없애고, 그 자리에 왼쪽에 시료, 오른쪽에 장비 이렇게 두 개 탭으로 … 디폴트
       *    탭은 시료 탭이야」. 장비 관점 콘솔은 이것을 안 넘기므로 **그림 탭 줄 그대로**다
       *    (eric — 「장비 console 까지 수정하진 마」).
       */
      layout="sampleTabs"
      /*
       * 🔴 **자동 전환은 `replace` 다.** 배치가 원심 → 어셈블러 → 염색기로 넘어갈 때마다
       *    이력이 쌓이면 사람이 「뒤로」를 눌렀을 때 **제가 가지도 않은 모듈들을 되짚는다.**
       *    이력에 남는 것은 사람이 «누른» 것뿐이다.
       */
      onFollowDevice={(key) => void navigate(deviceTo(key), { replace: true })}
      /*
       * 시료를 누르면 **오른쪽 판**에 그 시료 상세(eric 2026-09-27). 🔴 **경로를 아는 것은 라우트뿐이다** —
       * 화면은 라우터를 모른다. 모듈 키(`base`)를 유지한다
       */
      onOpenSample={(id) =>
        void navigate(id === selectedSampleId ? base : `${base}/sample/${encodeURIComponent(id)}`)
      }
      onSelectDevice={(key) => void navigate(deviceTo(key))}
      /*
       * 🔴 **시료 관점이 켜는 것**(eric 지시 2026-09-21 — 「사용자 입장에서 중요한 건 … 특정
       *    시료가 원심분리가 되었는지」). 장비 관점은 제 어댑터
       *    (`apps/console-equipment/.../EquipmentMonitoringRoute.tsx`)를 쓰고 이걸 안 켠다
       *    ⇒ 그쪽 화면은 한 픽셀도 안 바뀐다.
       */
      sampleFlow
    />
  )
}

/**
 * 새 작업 — 전체 화면 한 장(장비 관점 콘솔이 쓴다). 🔴 **컨테이너가 이동까지 쥔다** — 만든 뒤 상세로
 * 가는 길이 「만들어진 작업 id」에 달려 있어서 라우트가 미리 알 수 없다. 여기서는 컨테이너를 꽂기만 한다.
 */
export function NewBatchRoute() {
  return <BatchNewContainer />
}

/**
 * 새 작업 — **작업 목록 위에 뜨는 창**(eric 2026-09-22 「새 작업을 팝업창으로 한번 만들어봐」).
 * 주소는 종전 그대로 `/batches/new` 라 새로고침 · 뒤로가기가 산다. 닫으면(× · Esc · 바깥 누름 · 취소) 목록으로,
 * 「추가」로 만들어지면(201) 그 자리에서 닫히고 목록으로 간다(eric 2026-10-21 「작업 추가하면 창이 바로 닫혀야지」 · `BatchNewContainer`).
 * 🔴 덮개는 본문 범위(`scope="body"`)다 — 머리줄과 상시 작업 줄(E-STOP)은 창이 떠 있어도 눌린다.
 * ⚠ 페이지로 되돌리려면 라우트 표의 이 줄을 `NewBatchRoute` 로 바꾸면 끝이다.
 */
export function NewBatchModalRoute() {
  const navigate = useNavigate()
  return (
    <div className="splitpage">
      <BatchesRoute opsTab="batches" />
      {/*
        창은 추가 창 일곱이 `AddModal` 하나다(eric 2026-09-30 「창마다 포맷이 달라.. 다 통일해줘」) — 한 폭 · 한 열.
        바깥 누름으로도 닫힌다 — 🔴 만드는 중에만 잠긴다(`NewBatchScreen` 의 `useModalBusy`).
      */}
      <AddModal
        size="lg"
        onClose={() => void navigate(PATHS.batches)}
        title={t('screen.batchAdd.title')}
      >
        <BatchNewContainer frame="modal" />
      </AddModal>
    </div>
  )
}

/**
 * 작업 목록 + 오른쪽 상세 판(eric 2026-09-22 「작업에서 해당 열을 클릭하면 다른 상세 페이지로 가지 말고 오른쪽에서
 * 정보가 나타나게」). 주소는 종전 그대로(`/batches` · `/batches/:batchId`)라 새로고침·뒤로가기·작업 줄의 「작업 상세 보기」
 * 링크가 다 산다 — 바뀐 것은 **그 주소가 무엇을 그리는가**다: 목록은 늘 서고 `:batchId` 가 있으면 오른쪽 판이 열린다.
 * 🔴 상세 화면은 **같은 컨테이너**(`BatchDetailContainer`)다. 판에 맞춰 두 벌을 만들지 않는다.
 * 이력(`/history/:batchId`)도 같은 모양이다(`HistorySplitRoute` · eric 2026-09-22 「작업의 이력도 이와 같이」).
 */
export function BatchesSplitRoute() {
  const navigate = useNavigate()
  const { batchId } = useParams()
  return (
    <div className={cx('splitpage', batchId !== undefined && 'splitpage--open')}>
      <BatchesRoute
        opsTab="batches"
        {...(batchId === undefined ? {} : { selectedBatchId: batchId })}
      />
      {/* 서랍 — 다른 행을 누르면 같은 서랍의 내용만 바뀐다(`key` 로 판을 새로 세워 열림 움직임이 한 번 더 난다) */}
      {batchId === undefined ? null : (
        <aside aria-label={t('screen.batchDetail.title')} className="splitpage__pane" key={batchId}>
          <BatchDetailContainer
            batchId={batchId}
            hideEventLogDownload
            showEventLog
            localizeTrayPoints
            /* 🔴 왼쪽 목록이 이미 보이는 칸 — 상세에서 그 줄이 빠진다(eric 2026-09-23) */
            listedFields={BATCHES_LIST_FIELDS}
            onClose={() => void navigate(PATHS.batches)}
            /* 멈춘 모듈 → 모듈 상세(`HaltFacts` · 백엔드 0.28.15 `halt_module_id`) */
            onOpenModule={(moduleId) => void navigate(`${PATHS.modules}/${moduleId}`)}
          />
        </aside>
      )}
    </div>
  )
}

/**
 * 알람 목록 + 오른쪽 상세 서랍(eric 2026-09-28 「알람에서도 구조를 오른쪽으로.. 상세 페이지 보이게」).
 * 작업·시료·랩웨어와 같은 뼈대다 — `/alarms` · `/alarms/:alarmId` 두 주소가 같은 화면이고 `:alarmId` 가 있으면 서랍이 선다.
 * 🔴 목록의 탭 · 검색 · 분류 · 「미확인만」은 목록 컴포넌트의 상태라 서랍을 열고 닫아도 그대로다
 *    (두 주소가 같은 요소를 그려 React 가 목록을 새로 세우지 않는다).
 */
export function AlarmsSplitRoute() {
  const navigate = useNavigate()
  const { alarmId } = useParams()
  return (
    <div className={cx('splitpage', alarmId !== undefined && 'splitpage--open')}>
      <AlarmsContainer
        onOpenAlarm={(id) => void navigate(alarmDetailPath(id))}
        selectedAlarmId={alarmId}
      />
      {alarmId === undefined ? null : (
        <aside aria-label={t('screen.alarmDetail.title')} className="splitpage__pane" key={alarmId}>
          <AlarmDetailContainer alarmId={alarmId} onClose={() => void navigate(PATHS.alarms)} />
        </aside>
      )}
    </div>
  )
}

/**
 * 이력 목록 + 오른쪽 상세 판. 주소는 종전 그대로(`/history` · `/history/:batchId`)다.
 */
export function HistorySplitRoute() {
  const navigate = useNavigate()
  const { batchId } = useParams()
  /*
   * 🔴 **보고서 단추(목록 열 · 서랍 머리)는 2026-09-30 에 뺐다**(eric 「지금 화면에서.. 보고서 부분은 빼줘」).
   *    까닭 · 되살리는 조건은 `HistoryScreen.tsx` 머리말.
   */
  return (
    <div className={cx('splitpage', batchId !== undefined && 'splitpage--open')}>
      <HistoryRoute
        opsTab="history"
        {...(batchId === undefined ? {} : { selectedBatchId: batchId })}
      />
      {batchId === undefined ? null : (
        <aside aria-label={t('screen.batchDetail.title')} className="splitpage__pane" key={batchId}>
          <BatchDetailContainer
            batchId={batchId}
            showEventLog
            localizeTrayPoints
            /* 🔴 이력의 열은 작업 목록과 다르다 — 겹치는 것은 「기간」 하나다 */
            listedFields={HISTORY_LIST_FIELDS}
            onClose={() => void navigate(PATHS.batchHistory)}
          />
        </aside>
      )}
    </div>
  )
}

/**
 * 작업 상세 한 장(장비 관점 콘솔·태블릿이 쓴다). 통합 콘솔의 `/batches/:batchId` 는 위 `BatchesSplitRoute` 가 맡는다.
 * 돌아가는 줄이 「← 작업」이다.
 * 🔴 **컨테이너를 꽂는다**(2026-09-20). 종전에는 화면을 바로 꽂고 `DEMO_ACCESS` 를 박았는데,
 *    그래서 상세만 실물이 아니었다(목록·새 작업은 벌써 live 였다).
 */
export function BatchDetailRoute() {
  const navigate = useNavigate()
  return (
    <BatchDetailContainer
      backLabel={t('screen.batches.title')}
      onBack={() => void navigate(PATHS.batches)}
    />
  )
}

/**
 * 이력에서 열었을 때 — 돌아가는 줄이 「← 이력」이다.
 * 와이어프레임의 `cur.bfrom`(2921행)을 **라우트로 옮긴 것**이다. 같은 상세를 두 경로가 쓴다.
 */
export function HistoryDetailRoute() {
  const navigate = useNavigate()
  return (
    <BatchDetailContainer
      backLabel={t('screen.batchHistory.title')}
      onBack={() => void navigate(PATHS.batchHistory)}
    />
  )
}
